/**
 * CreditEngineReviewQueue — /admin/credit-engine/review-queue
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern from
 * CreditEngineEvents applied verbatim. STATUS_COLOR-style pastels
 * removed; reason chips use `Badge shape="sharp" tone="danger"`.
 * Action buttons (Dismiss / Escalate / Resolve) drop hex hover
 * colors and adopt tokenised GhostButton with sharp radii.
 *
 * Business logic (V2.1 Phase 2 Section K review queue + decide PATCH)
 * preserved BYTE-EXACT. All testids unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, ArrowUpRight, Filter, Inbox } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Badge } from "@/design-system/components/Badge";
import { GhostButton, Section, SelectInput, Stat } from "@/admin/_primitives";

const STATUS_OPTIONS = ["open", "dismissed", "escalated", "resolved"];

const STATUS_TONE = {
  open: "warning",
  dismissed: "neutralMuted",
  escalated: "danger",
  resolved: "successMuted",
};

/* Local admin primitives (mirror Credit Engine anchor). */




export default function AdminCreditEngineReviewQueue() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [reasonFilter, setReasonFilter] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (reasonFilter) params.set("reason", reasonFilter);
      params.set("limit", "50");
      const [{ data }, { data: statsData }] = await Promise.all([
        api.get(`/admin/credit-engine/review-queue?${params.toString()}`),
        api.get(`/admin/credit-engine/review-queue/stats`),
      ]);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setStats(statsData || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const decide = async (item, action) => {
    try {
      await api.patch(`/admin/credit-engine/review-queue/${item.id}`, { action });
      toast.success(`Item ${action}d`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminLayout
      title="V2 review queue"
      subtitle={`${stats?.total_open ?? 0} open · ${total} in current view`}
      actions={
        <GhostButton onClick={reload} data-testid="review-queue-refresh" className="!py-1 !text-[11px]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </GhostButton>
      }
    >
      <div className="mb-3 max-w-3xl" data-testid="review-queue-help">
        <AdminPageHelp
          title="V2 review queue"
          purpose="V2.1 Phase 2 Section K. Every credit-report import whose V2 pipeline disagrees materially with the legacy pipeline (account count Δ>2%, negative-count mismatch, bureau preservation failed, missing tradelines, validation failed, processing errored) is auto-flagged here. Use this queue to spot systematic issues before the cutover."
          configures="Item status (dismiss / escalate / resolve). All decisions are audit-logged."
          impact="No user-visible impact. Read-only against the credit_reports_v2 stack."
          safety="FCRA-audited: every dismissal / escalation / resolution writes an admin_audit_logs row."
        />
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {STATUS_OPTIONS.map((s, idx) => (
            <Stat
              key={s}
              testid={`review-stat-${s}`}
              label={s}
              value={stats.by_status?.[s] ?? 0}
              highlight={idx === 0}
            />
          ))}
        </div>
      )}

      <Section
        title="Filter"
        icon={Filter}
        testid="review-queue-filters"
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Status
            </span>
            <SelectInput
              data-testid="review-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </SelectInput>
          </label>
          <label className="block">
            <span
              className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Reason
            </span>
            <SelectInput
              data-testid="review-reason-filter"
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
            >
              <option value="">All reasons</option>
              {Object.keys(stats?.by_reason || {}).map((r) => (
                <option key={r} value={r}>{r} ({stats.by_reason[r]})</option>
              ))}
            </SelectInput>
          </label>
        </div>
      </Section>

      {loading ? (
        <div
          data-testid="review-queue-loading"
          className="text-[13px] py-6 text-center"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading…
        </div>
      ) : (
        <Section
          title="Review items"
          icon={Inbox}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {items.length} showing · {total} total
            </span>
          }
          testid="review-queue-table-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto" data-testid="review-queue-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2 font-semibold">Created</th>
                  <th className="px-4 py-2 font-semibold">Report</th>
                  <th className="px-4 py-2 font-semibold">Reasons</th>
                  <th className="px-4 py-2 font-semibold">Evidence</th>
                  <th className="px-4 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isLast = idx === items.length - 1;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`review-row-${row.id}`}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                      }}
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] tabular-nums font-mono whitespace-nowrap"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at ? row.created_at.slice(0, 19).replace("T", " ") : "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[11px] font-mono truncate max-w-[180px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {String(row.report_id).slice(-10)}
                      </td>
                      <td className="px-4 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {(row.reasons || []).map((r) => (
                            <Badge key={r} tone="danger" size="sm" shape="sharp" className="tracking-[0.1em]">
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td
                        className="px-4 py-1.5 text-[11px] font-mono max-w-[260px] truncate"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        accts L:{row.evidence?.legacy_account_count ?? "—"} / V2:{row.evidence?.v2_account_count ?? "—"}
                        {row.evidence?.missing_tradelines ? ` · miss:${row.evidence.missing_tradelines}` : ""}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        {row.status === "open" ? (
                          <div className="flex justify-end gap-1">
                            <GhostButton
                              data-testid={`review-dismiss-${row.id}`}
                              onClick={() => decide(row, "dismiss")}
                              className="!px-2 !py-1 !text-[10px]"
                              title="Dismiss — a known / expected variance"
                            >
                              <XCircle className="w-3 h-3" /> Dismiss
                            </GhostButton>
                            <GhostButton
                              data-testid={`review-escalate-${row.id}`}
                              onClick={() => decide(row, "escalate")}
                              className="!px-2 !py-1 !text-[10px]"
                              title="Escalate — flag for engineering"
                            >
                              <ArrowUpRight className="w-3 h-3" /> Escalate
                            </GhostButton>
                            <GhostButton
                              data-testid={`review-resolve-${row.id}`}
                              onClick={() => decide(row, "resolve")}
                              className="!px-2 !py-1 !text-[10px]"
                              title="Resolve — the pipeline fix has landed"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Resolve
                            </GhostButton>
                          </div>
                        ) : (
                          <Badge
                            tone={STATUS_TONE[row.status] || "neutralMuted"}
                            size="sm"
                            shape="sharp"
                            className="uppercase tracking-[0.14em]"
                          >
                            {row.status}{row.resolved_at ? ` · ${row.resolved_at.slice(0, 10)}` : ""}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No review queue items match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </AdminLayout>
  );
}
